import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { 
  Shield, Users, UserPlus, UserMinus, Crown, Calendar, 
  Check, X, Search, RefreshCw, AlertTriangle, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import './AdminPanel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPanel() {
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [subscriptionModal, setSubscriptionModal] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    durationType: '1month',
    customDays: 30
  });

  // Check admin status on mount
  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, { withCredentials: true });
      if (response.data.is_admin) {
        setIsAdmin(true);
        loadData();
      } else {
        // Not admin - redirect to dashboard
        navigate('/dashboard');
        toast.error('Access denied. Admin privileges required.');
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [usersRes, adminsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/admins`, { withCredentials: true })
      ]);
      setUsers(usersRes.data.users || []);
      setAdmins(adminsRes.data.admins || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      await axios.post(`${API}/admin/admins/add`, 
        { email: newAdminEmail },
        { withCredentials: true }
      );
      toast.success(`Admin access granted to ${newAdminEmail}`);
      setNewAdminEmail('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add admin');
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (!window.confirm(`Remove admin access from ${email}?`)) return;

    try {
      await axios.post(`${API}/admin/admins/remove`,
        { email },
        { withCredentials: true }
      );
      toast.success(`Admin access removed from ${email}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const openSubscriptionModal = (user) => {
    setSelectedUser(user);
    setSubscriptionForm({
      durationType: '1month',
      customDays: 30,
      maxSavedGames: user.max_saved_games || 3
    });
    setSubscriptionModal(true);
  };

  const handleUpdateSubscription = async (isPremium) => {
    if (!selectedUser) return;

    try {
      await axios.post(`${API}/admin/subscription/update`, {
        user_id: selectedUser.id,
        is_premium: isPremium,
        subscription_type: isPremium ? 'paid' : 'free',
        duration_type: subscriptionForm.durationType,
        custom_days: subscriptionForm.durationType === 'custom' ? subscriptionForm.customDays : null
      }, { withCredentials: true });

      toast.success(isPremium ? 'Premium subscription activated!' : 'Subscription deactivated');
      setSubscriptionModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleUpdateMaxGames = async () => {
    if (!selectedUser) return;

    try {
      await axios.post(`${API}/admin/user/max-games`, {
        user_id: selectedUser.id,
        max_saved_games: subscriptionForm.maxSavedGames
      }, { withCredentials: true });

      toast.success(`Max saved games updated to ${subscriptionForm.maxSavedGames}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update game limit');
    }
  };

  const handleExtendSubscription = async (userId, days) => {
    try {
      await axios.post(`${API}/admin/subscription/extend`, {
        user_id: userId,
        additional_days: days
      }, { withCredentials: true });

      toast.success(`Subscription extended by ${days} days`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend subscription');
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSubscriptionStatus = (user) => {
    if (!user.is_premium) return { status: 'free', label: 'Free', color: 'gray' };
    
    if (user.subscription_expiry_date) {
      const expiry = new Date(user.subscription_expiry_date);
      const now = new Date();
      const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
      
      if (daysLeft <= 0) return { status: 'expired', label: 'Expired', color: 'red' };
      if (daysLeft <= 7) return { status: 'expiring', label: `${daysLeft}d left`, color: 'orange' };
      return { status: 'active', label: `${daysLeft}d left`, color: 'green' };
    }
    
    return { status: 'active', label: 'Active', color: 'green' };
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <RefreshCw className="animate-spin" size={48} />
        <p>Verifying admin access...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="admin-title">
          <Shield size={32} className="admin-icon" />
          <h1>Admin Panel</h1>
        </div>
        <p className="admin-subtitle">Manage users and subscriptions</p>
      </div>

      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={18} />
          Users ({users.length})
        </button>
        <button 
          className={`admin-tab ${activeTab === 'admins' ? 'active' : ''}`}
          onClick={() => setActiveTab('admins')}
        >
          <Crown size={18} />
          Admins ({admins.length})
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="admin-section">
          <div className="section-header">
            <h2>All Users</h2>
            <div className="search-box">
              <Search size={18} />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button onClick={loadData} variant="outline" size="sm">
              <RefreshCw size={16} />
              Refresh
            </Button>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Subscription</th>
                  <th>Saved Games</th>
                  <th>Played Games</th>
                  <th>Expiry Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => {
                  const subStatus = getSubscriptionStatus(u);
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="user-cell">
                          <img 
                            src={u.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || 'U')}&background=random`} 
                            alt={u.name}
                            className="user-avatar"
                          />
                          <span>{u.name}</span>
                        </div>
                      </td>
                      <td>{u.email}</td>
                      <td>
                        <span className={`role-badge ${u.is_admin ? 'admin' : 'user'}`}>
                          {u.is_admin ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td>
                        <span className={`subscription-badge ${subStatus.status}`}>
                          {u.is_premium ? '⭐ Premium' : 'Free'}
                        </span>
                      </td>
                      <td>
                        <span className="games-count">
                          {u.is_premium ? '∞' : `${u.saved_games_count || 0}/${u.max_saved_games || 3}`}
                        </span>
                      </td>
                      <td>
                        <span className="games-count">
                          {u.is_premium ? '∞' : `${u.played_games_count || 0}/${u.max_played_games || 3}`}
                        </span>
                      </td>
                      <td>
                        <span className={`expiry-badge ${subStatus.color}`}>
                          {u.is_premium ? subStatus.label : '-'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openSubscriptionModal(u)}
                          >
                            <Calendar size={14} />
                            Manage
                          </Button>
                          {u.is_premium && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleExtendSubscription(u.id, 30)}
                              title="Extend by 30 days"
                            >
                              +30d
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'admins' && (
        <div className="admin-section">
          <div className="section-header">
            <h2>Admin Management</h2>
          </div>

          <div className="add-admin-form">
            <h3><UserPlus size={18} /> Add New Admin</h3>
            <div className="form-row">
              <Input
                placeholder="Enter user email..."
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddAdmin()}
              />
              <Button onClick={handleAddAdmin}>
                <UserPlus size={16} />
                Add Admin
              </Button>
            </div>
          </div>

          <div className="admins-list">
            <h3>Current Admins</h3>
            {admins.map(admin => (
              <div key={admin.id} className="admin-card">
                <div className="admin-info">
                  <img 
                    src={admin.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(admin.name || 'A')}&background=random`}
                    alt={admin.name}
                    className="admin-avatar"
                  />
                  <div>
                    <p className="admin-name">{admin.name}</p>
                    <p className="admin-email">{admin.email}</p>
                  </div>
                  {admin.email?.toLowerCase() === 'issam7036@gmail.com' && (
                    <span className="super-admin-badge">
                      <Crown size={14} /> Super Admin
                    </span>
                  )}
                </div>
                {admin.email?.toLowerCase() !== 'issam7036@gmail.com' && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleRemoveAdmin(admin.email)}
                  >
                    <UserMinus size={14} />
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {subscriptionModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setSubscriptionModal(false)}>
          <div className="subscription-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Subscription</h3>
              <button className="close-btn" onClick={() => setSubscriptionModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-content">
              <div className="user-info-card">
                <img 
                  src={selectedUser.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.name || 'U')}&background=random`}
                  alt={selectedUser.name}
                />
                <div>
                  <p className="user-name">{selectedUser.name}</p>
                  <p className="user-email">{selectedUser.email}</p>
                </div>
              </div>

              <div className="current-status">
                <h4>Current Status</h4>
                <p>
                  <strong>Type:</strong> {selectedUser.is_premium ? 'Premium (Unlimited)' : 'Free'}
                </p>
                <p>
                  <strong>Saved Games:</strong> {selectedUser.saved_games_count || 0} / {selectedUser.is_premium ? '∞' : (selectedUser.max_saved_games || 3)}
                </p>
                {selectedUser.is_premium && selectedUser.subscription_expiry_date && (
                  <p>
                    <strong>Expires:</strong> {formatDate(selectedUser.subscription_expiry_date)}
                  </p>
                )}
              </div>

              {/* Game Limit Settings for Free Users */}
              {!selectedUser.is_premium && (
                <div className="game-limit-section">
                  <h4>Game Limit Override</h4>
                  <p className="section-desc">Set custom max saved games for this free user</p>
                  <div className="game-limit-row">
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={subscriptionForm.maxSavedGames}
                      onChange={(e) => setSubscriptionForm({
                        ...subscriptionForm,
                        maxSavedGames: parseInt(e.target.value) || 3
                      })}
                      style={{ width: '100px' }}
                    />
                    <Button size="sm" onClick={handleUpdateMaxGames}>
                      Update Limit
                    </Button>
                  </div>
                </div>
              )}

              <div className="subscription-options">
                <h4>Subscription Duration</h4>
                <div className="duration-buttons">
                  {[
                    { key: '1month', label: '1 Month' },
                    { key: '2months', label: '2 Months' },
                    { key: '3months', label: '3 Months' },
                    { key: '6months', label: '6 Months' },
                    { key: '1year', label: '1 Year' },
                    { key: 'custom', label: 'Custom' }
                  ].map(opt => (
                    <button
                      key={opt.key}
                      className={`duration-btn ${subscriptionForm.durationType === opt.key ? 'active' : ''}`}
                      onClick={() => setSubscriptionForm({...subscriptionForm, durationType: opt.key})}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {subscriptionForm.durationType === 'custom' && (
                  <div className="custom-days">
                    <label>Custom Duration (days):</label>
                    <Input
                      type="number"
                      min="1"
                      max="3650"
                      value={subscriptionForm.customDays}
                      onChange={(e) => setSubscriptionForm({
                        ...subscriptionForm,
                        customDays: parseInt(e.target.value) || 1
                      })}
                    />
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <Button 
                  className="activate-btn"
                  onClick={() => handleUpdateSubscription(true)}
                >
                  <Check size={16} />
                  Activate Premium
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => handleUpdateSubscription(false)}
                >
                  <X size={16} />
                  Deactivate / Set Free
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
